import { DEFAULT_PAGE_SIZE, UNIFIED_ORDER_STATUS, unifiedStatusFromOrder, type OrderDetail } from '@flux/shared';
import { ExternalLink } from 'lucide-react';
import type { JSX } from 'react';
import { PageHeader } from '@/components/layout/page.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { OriginBadge } from '@/components/filters/origin-badge.js';
import { Button } from '@/components/ui/button.js';
import { OrderActionDialogs } from './order-action-dialogs.js';
import { isFixedByShopifyRefresh } from './order-blocked-banner.js';
import { ORDER_ACTION_ICONS, ORDER_ACTION_LABELS } from './order-row-actions.js';
import type { OrderRowAction } from './order-status-actions.js';
import {
  orderActionTargetFromDetail,
  useOrderActionController,
} from './use-order-action-controller.js';
import { V1Badge, V1Notice } from './v1-badge.js';

/**
 * Action mise en avant (bouton plein) quand elle est proposée : celle qui fait
 * avancer la commande. Ces trois actions dépendent de statuts exclusifs
 * (`01` §4), il n'y en a donc jamais plus d'une à la fois. « Mettre à jour
 * depuis Shopify », le renvoi à Martelet et « Marquer ignorée » restent
 * secondaires.
 */
const PRIMARY_ACTIONS: readonly OrderRowAction[] = ['send-now', 'mark-handled', 'reactivate'];

export interface OrderHeaderProps {
  order: OrderDetail;
}

/** En-tête du détail commande (`02` §4) : identité, statuts, montants, lien Shopify, boutons d'action selon statut. */
export function OrderHeader({ order }: OrderHeaderProps): JSX.Element {
  const controller = useOrderActionController(orderActionTargetFromDetail(order));
  const fixed = isFixedByShopifyRefresh(order);
  // Une fois corrigée, l'action qui débloque la commande est mise en avant dans
  // l'encart vert (`order-blocked-banner.tsx`) : pas de second bouton plein ici.
  const primaryActions = fixed ? [] : PRIMARY_ACTIONS;

  return (
    <>
      <PageHeader
        backLink={{
          label: 'Retour aux commandes',
          to: '/commandes',
          search: { page: 1, pageSize: DEFAULT_PAGE_SIZE, sortDir: 'desc' },
        }}
        title={order.name}
        status={<span className="flex items-center gap-2"><StatusBadge registry={UNIFIED_ORDER_STATUS} code={unifiedStatusFromOrder(order.status)} />{order.simulated ? <Badge tone="orange">Simulé</Badge> : null}<V1Badge handledByV1={order.handledByV1} />{fixed ? <Badge tone="green">Corrigée, à renvoyer</Badge> : null}</span>}
        description={
          <span className="flex flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span>
                Origine <OriginBadge code={order.store} label={order.storeName} />
              </span>
              <span aria-hidden="true">·</span>
              <span className="min-w-0 break-words">
                {order.customerName ?? order.email ?? 'Client inconnu'}
              </span>
              {order.amounts ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    Total TTC :{' '}
                    <span className="font-medium tabular-nums text-foreground">
                      {order.amounts.totalInclTax} {order.amounts.currency}
                    </span>
                  </span>
                </>
              ) : null}
            </span>
            <V1Notice handledByV1={order.handledByV1} />
          </span>
        }
        actions={
          <>
            <Button asChild variant="outline">
              <a href={order.shopifyAdminUrl} target="_blank" rel="noreferrer">
                <ExternalLink aria-hidden="true" />
                Voir dans Shopify
              </a>
            </Button>
            {controller.actions.map((action) => (
              <Button
                key={action}
                type="button"
                variant={primaryActions.includes(action) ? 'default' : 'outline'}
                disabled={controller.isBusy}
                onClick={() => {
                  controller.trigger(action);
                }}
              >
                {ORDER_ACTION_ICONS[action]}
                {ORDER_ACTION_LABELS[action]}
              </Button>
            ))}
          </>
        }
      />

      <OrderActionDialogs controller={controller} />
    </>
  );
}
